import React, {useState, useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {message, Alert} from "antd";
import {CheckCircleFilled} from "@ant-design/icons";
import moment from "moment";
import {useStore} from "hooks/useStore";
import ShiftSelection from "./ShiftSelection";
import DateSelection from "./DateSelection";
import CourierPackageCountInput from "./CourierPackageCountInput";
import RequestDetailModal from "./RequestDetailModal";
import CourierRequestConfirmationModal from "./CourierRequestConfirmationModal";
import {
    StepsContainer,
    StepItem,
    StepNumber,
    StepDescription,
    ContentContainer,
    ActionButtonContainer,
    SubmitButton
} from "../style/CustomerRequestsPage.styles";

const CourierIncreaseRequest = ({
    refetchTable,
}) => {
    const {t} = useTranslation();
    const stores = useStore();
    const [currentStep, setCurrentStep] = useState(1);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [courierCount, setCourierCount] = useState(null);
    const [packageCount, setPackageCount] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingRequestData, setPendingRequestData] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [requestLoading, setRequestLoading] = useState(false);
    const [requestData, setRequestData] = useState(null);

    // Adım 1 tamamlandığında otomatik geçiş
    useEffect(() => {
        if (startTime && endTime && currentStep === 1) {
            setCurrentStep(2);
        }
    }, [startTime, endTime, currentStep]);

    useEffect(() => {
        if (startDate && currentStep === 2) {
            setCurrentStep(3);
        }
    }, [startDate, currentStep]);

    useEffect(() => {
        if (requestData !== null && pendingRequestData && !requestLoading) {
            if (requestData === true) {
                message.success(t("screens.customerRequests.requestCreatedSuccess"));
                setStartTime(null);
                setEndTime(null);
                setStartDate(null);
                setEndDate(null);
                setCourierCount(null);
                setPackageCount(null);
                setCurrentStep(1);
                refetchTable();
            }
            setShowConfirmationModal(false);
            setPendingRequestData(null);
            setRequestData(null);
        }
    }, [requestData, pendingRequestData, requestLoading, refetchTable, t]);

    const handleSubmit = () => {
        if (!startTime || !endTime) {
            message.warning(t("screens.customerRequests.pleaseSelectShiftHours"));
            return;
        }

        const totalHours = getTotalHours();
        if (totalHours < 10 || totalHours > 13) {
            message.warning(t("screens.customerRequests.shiftHoursMustBeBetween10And13"));
            return;
        }
        if (!startDate) {
            message.warning(t("screens.customerRequests.pleaseSelectStartDate"));
            return;
        }
        if (!courierCount || courierCount < 1 || courierCount > 10) {
            message.warning(t("screens.customerRequests.pleaseEnterValidCourierCount"));
            return;
        }
        if (!packageCount || packageCount < 1) {
            message.warning(t("screens.customerRequests.pleaseEnterValidPackageCount"));
            return;
        }

        const requestData = {
            startDate,
            endDate,
            startTime,
            endTime,
            courierCount,
            packageCount: packageCount || 0
        };

        setPendingRequestData(requestData);
        setShowConfirmationModal(true);
    };

    const handleConfirmRequest = async () => {
        if (!pendingRequestData) return;

        setRequestLoading(true);
        setRequestData(null);

        try {
            const params = {
                subCompanyId:
                    stores?.meStore?.selectedSubCompanyId || stores.userStore.user?.subCompanyId,
                type: "planning",
                subType: "increment",
                startDate: moment(pendingRequestData.startDate).format("YYYY-MM-DD"),
                endDate: moment(pendingRequestData.endDate || pendingRequestData.startDate).format(
                    "YYYY-MM-DD"
                ),
                startHour: pendingRequestData.startTime,
                endHour: pendingRequestData.endTime,
                courierCount: pendingRequestData.courierCount,
                packageCount: pendingRequestData.packageCount
            };

            const result = await stores.shiftRequestStore.createShiftRequest(params);
            setRequestData(result);
        } catch (error) {
            setRequestData(false);
        } finally {
            setRequestLoading(false);
        }
    };

    const handleCancelRequest = () => {
        setShowConfirmationModal(false);
        setPendingRequestData(null);
    };

    const handleViewDetails = request => {
        setSelectedRequest(request);
        setDetailModalVisible(true);
    };

    const handleCloseDetailModal = () => {
        setDetailModalVisible(false);
        setSelectedRequest(null);
    };

    const getTotalHours = () => {
        if (!startTime || !endTime) return 0;

        const start = parseInt(startTime.split(":")[0]);
        const end = parseInt(endTime.split(":")[0]);

        if (end > start) {
            return end - start;
        } else if (end < start) {
            return 24 - start + end;
        }
        return 0;
    };

    const getTotalDays = () => {
        if (!startDate) return 0;
        if (!endDate) return 1;
        const start = moment(startDate).startOf("day");
        const end = moment(endDate).startOf("day");
        return end.diff(start, "days") + 1;
    };

    const isStepCompleted = step => {
        switch (step) {
            case 1:
                return startTime && endTime;
            case 2:
                return startDate;
            case 3:
                return (
                    courierCount !== null &&
                    courierCount > 0 &&
                    packageCount !== null &&
                    packageCount > 0
                );
            default:
                return false;
        }
    };

    return (
        <>
            <Alert
                type="warning"
                showIcon
                message={t("screens.customerRequests.onlyOneRequestPerDay")}
                style={{marginBottom: "16px"}}
            />
            <StepsContainer>
                <StepItem>
                    <StepNumber $completed={isStepCompleted(1)} $active={currentStep >= 1}>
                        {isStepCompleted(1) ? <CheckCircleFilled /> : 1}
                    </StepNumber>
                    <StepDescription>
                        {t("screens.customerRequests.shiftSelection")}
                    </StepDescription>
                </StepItem>
                <StepItem>
                    <StepNumber $completed={isStepCompleted(2)} $active={currentStep >= 2}>
                        {isStepCompleted(2) ? <CheckCircleFilled /> : 2}
                    </StepNumber>
                    <StepDescription>{t("screens.customerRequests.dateSelection")}</StepDescription>
                </StepItem>
                <StepItem>
                    <StepNumber $completed={isStepCompleted(3)} $active={currentStep >= 3}>
                        {isStepCompleted(3) ? <CheckCircleFilled /> : 3}
                    </StepNumber>
                    <StepDescription>
                        {t("screens.customerRequests.courierPackageCount")}
                    </StepDescription>
                </StepItem>
            </StepsContainer>

            <ContentContainer>
                <ShiftSelection
                    startTime={startTime}
                    setStartTime={setStartTime}
                    endTime={endTime}
                    setEndTime={setEndTime}
                    totalHours={getTotalHours()}
                    isActive={true}
                />

                <DateSelection
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    totalDays={getTotalDays()}
                    isActive={isStepCompleted(1)}
                    showEndDate={false}
                />

                <CourierPackageCountInput
                    courierCount={courierCount}
                    setCourierCount={setCourierCount}
                    packageCount={packageCount}
                    setPackageCount={setPackageCount}
                    isActive={isStepCompleted(1) && isStepCompleted(2)}
                />
            </ContentContainer>

            <ActionButtonContainer>
                <SubmitButton
                    onClick={handleSubmit}
                    disabled={!isStepCompleted(1) || !isStepCompleted(2) || !isStepCompleted(3)}
                >
                    {t("screens.customerRequests.submitRequest")}
                </SubmitButton>
            </ActionButtonContainer>

            <CourierRequestConfirmationModal
                visible={showConfirmationModal}
                onCancel={handleCancelRequest}
                onConfirm={handleConfirmRequest}
                requestData={pendingRequestData}
                loading={requestLoading}
            />

            <RequestDetailModal
                visible={detailModalVisible}
                onClose={handleCloseDetailModal}
                requestData={selectedRequest}
                refetchTable={refetchTable}
            />
        </>
    );
};

export default observer(CourierIncreaseRequest);
